import React, { type ReactNode, useState, useEffect } from 'react';
import { LockIcon } from '@mentimeter/ragnar-visuals';
import { useTranslations } from 'next-intl';
import { PaymentMethodType } from '@mentimeter/http-clients';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button } from '@mentimeter/ragnar-ui/button';
import { Link } from '@mentimeter/ragnar-ui/link';
import { Text } from '@mentimeter/ragnar-ui/text';
import { type SubscriptionUpdatePreviewT } from '@mentimeter/billing';
import { TriggerDescriptive, Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { useMatch } from '@mentimeter/ragnar-device';
import { captureMessage } from '@mentimeter/errors/sentry';
import { PurchaseSummaryLoading } from './PurchaseSummaryLoading';
import { getFormattedPlanName } from './utils';

interface PurchaseSummaryProps {
  isLoading: boolean;
  invoicePreview: SubscriptionUpdatePreviewT | undefined;
  fetchingError: Error | undefined;
  purchaseLoading: boolean;
  purchaseError: string;
  paymentMethod?: PaymentMethodType;
  onSubmit: () => void;
}

const BoldText = (chunks: ReactNode) => (
  <Text variant="dashboardBodySm" fontWeight="bold" color="textWeaker">
    {chunks}
  </Text>
);

const TermsLink = (chunks: ReactNode) => (
  <Link color="text" href="/terms" target="_blank" rel="noopener noreferrer">
    {chunks}
  </Link>
);

export const PurchaseSummary = ({
  isLoading,
  purchaseLoading,
  fetchingError,
  paymentMethod,
  invoicePreview,
  purchaseError,
  onSubmit,
}: PurchaseSummaryProps) => {
  const t = useTranslations('purchasing.legacy.checkoutUiPackage');
  const [timeoutError, setTimeoutError] = useState(false);

  const isBankTransferPaymentMethod =
    paymentMethod === PaymentMethodType.BankAccount ||
    paymentMethod === PaymentMethodType.VirtualAccount;
  const isMobile = useMatch({ lessThan: 2 });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!invoicePreview && !isLoading) {
      timer = setTimeout(() => setTimeoutError(true), 6000);
    }

    return () => clearTimeout(timer);
  }, [invoicePreview, isLoading]);

  if (isLoading) {
    return <PurchaseSummaryLoading paymentMethod={paymentMethod} />;
  }

  if (!invoicePreview) {
    if (timeoutError) {
      // We've had some issues with the invoice preview not loading/fetching, so we're logging it to Sentry
      captureMessage('No invoice preview in purchase summary', {
        level: 'error',
        tags: {
          fetchingError: String(fetchingError) ?? 'No error to log',
          hasInvoicePreview: String(!!invoicePreview),
        },
      });
    }

    // If for some reason the invoice preview is not loaded,
    // we want to show an error message telling them to try again and contact support if the problem persists
    return (
      <>
        <PurchaseSummaryLoading paymentMethod={paymentMethod} />
        {timeoutError && (
          <Text color="textNegative" variant="dashboardBodyMd" mt="space4">
            {t('checkout_modal.timeout_error')}
          </Text>
        )}
      </>
    );
  }

  const {
    purchaseItem,
    prorationItem,
    taxPercentage,
    tax,
    total,
    daysUntilSubscriptionEnds,
  } = invoicePreview;

  const isChangingBillingCycle =
    prorationItem.interval !== purchaseItem.interval;

  return (
    <Box>
      <Box
        flexDirection="row"
        width="100%"
        mb="space6"
        justifyContent="space-between"
      >
        <Box flex="1" minWidth="0">
          <Text
            fontWeight="semiBold"
            variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
          >
            {t('checkout_modal.summary_section.plan_name_licenses', {
              quantity: purchaseItem.quantity.toString(),
              planName: getFormattedPlanName(purchaseItem.category, true),
              license: t('checkout_modal.summary_section.licenses', {
                count: purchaseItem.quantity,
              }),
            })}
          </Text>
          {isChangingBillingCycle ? (
            <Text variant={isMobile ? 'dashboardBodyXs' : 'dashboardBodySm'}>
              {t('checkout_modal.summary_section.billed_yearly')}
            </Text>
          ) : (
            <Text
              maxWidth="95%"
              variant={isMobile ? 'dashboardBodyXs' : 'dashboardBodySm'}
              style={{ whiteSpace: 'pre-line' }}
            >
              {t('checkout_modal.summary_section.remainder_of_billing_cycle')}
              <Tooltip
                side="top"
                className="max-w-[300px]"
                trigger={<TriggerDescriptive className="cursor-help ml-1" />}
              >
                {t(
                  'checkout_modal.summary_section.cost_of_plan_until_renewal',
                  {
                    licenses: t('checkout_modal.summary_section.licenses', {
                      count: purchaseItem.quantity,
                    }),
                    days: daysUntilSubscriptionEnds.toString(),
                  },
                )}
              </Tooltip>
            </Text>
          )}
        </Box>
        <Text
          fontWeight="semiBold"
          textAlign="end"
          variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
        >
          {purchaseItem.amount}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        width="100%"
        mb="space6"
        justifyContent="space-between"
      >
        <Box flex="1" minWidth="0">
          <Text
            fontWeight="semiBold"
            maxWidth="95%"
            variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
            style={{ whiteSpace: 'pre-line' }}
          >
            {t('checkout_modal.summary_section.unused_time_refund')}
            <Tooltip
              side="top"
              className="max-w-[300px]"
              trigger={<TriggerDescriptive className="cursor-help ml-1" />}
            >
              {t('checkout_modal.summary_section.unused_time_refund_info', {
                days: daysUntilSubscriptionEnds.toString(),
                unusedQuantity: prorationItem.quantity.toString(),
                planName: getFormattedPlanName(prorationItem.category, false),
                license: t('checkout_modal.summary_section.licenses', {
                  count: prorationItem.quantity,
                }),
              })}
            </Tooltip>
          </Text>
        </Box>
        <Text
          fontWeight="semiBold"
          textAlign="end"
          color="positive"
          variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
        >
          {prorationItem.amount}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        width="100%"
        mb="space6"
        justifyContent="space-between"
      >
        <Box flex="1" minWidth="0">
          <Text
            fontWeight="semiBold"
            maxWidth="95%"
            variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
          >
            {t('tax')} ({taxPercentage})
          </Text>
        </Box>
        <Text
          fontWeight="semiBold"
          textAlign="end"
          variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
        >
          {tax}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        width="100%"
        py="space6"
        justifyContent="space-between"
        borderTopWidth="1px"
        borderStyle="dotted"
        borderColor="borderStrong"
      >
        <Box flex="1" minWidth="0">
          <Text
            fontWeight="semiBold"
            variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
            maxWidth="95%"
          >
            {isBankTransferPaymentMethod
              ? t('total_invoice_due_days')
              : t('total_due_today')}
          </Text>
        </Box>
        <Text
          fontWeight="semiBold"
          textAlign="end"
          color="positive"
          variant={isMobile ? 'dashboardBodySm' : 'dashboardBodyMd'}
        >
          {total}
        </Text>
      </Box>
      <Text
        color="textWeaker"
        variant="dashboardBodySm"
        whiteSpace="pre-line"
        width="100%"
      >
        {t.rich('auto_renewal_info', {
          licenses: t('licenses', { count: purchaseItem.quantity }),
          billingDate: invoicePreview.renewalDate,
          total: invoicePreview.renewalTotal,
          bold: BoldText,
        })}
      </Text>
      <Button
        width="100%"
        mt="space6"
        variant="primary"
        state={purchaseLoading ? 'loading' : undefined}
        disabled={Boolean(purchaseError) || purchaseLoading}
        iconLeading={LockIcon}
        onClick={onSubmit}
      >
        {isBankTransferPaymentMethod
          ? t('generate_invoice')
          : t('confirm_and_pay_button')}
      </Button>
      <Box minHeight={16} bottom={0} width="100%" alignItems="center">
        <Text fontSize="87.5" color="textNegative">
          {purchaseError}
        </Text>
      </Box>
      <Text variant="dashboardBodyXs" color="textWeaker" mt="space2">
        {t.rich('confirm_terms', { termsLink: TermsLink })}
      </Text>
    </Box>
  );
};
